import { useMutation, useQueryClient } from '@tanstack/react-query'
import { produce } from 'immer'
import { contentVersions } from '../../v3/contentVersions'
import type { ReadContentVersionsDocument } from '../../v3/contentVersions/types'
import { globalQueryKeys } from '../queryKeys'

type HideParams = { id: string }

/**
 * Soft-hides a version with an optimistic removal from the cached collection,
 * rolling back on error. Mirrors useUpdateContentVersionLabel: the optimistic
 * write targets the raw JSON:API document the query caches; the read hook's
 * `select` re-derives the flattened rail from it.
 */
export const useHideContentVersion = (contentUuid: string) => {
  const queryClient = useQueryClient()
  const queryKey = globalQueryKeys.contentVersions(contentUuid)

  return useMutation({
    mutationFn: ({ id }: HideParams) => contentVersions.hide({ id }),
    onMutate: async ({ id }: HideParams) => {
      await queryClient.cancelQueries(queryKey)
      const previous =
        queryClient.getQueryData<ReadContentVersionsDocument>(queryKey)

      queryClient.setQueryData<ReadContentVersionsDocument>(
        queryKey,
        (document) =>
          produce(document, (draft) => {
            if (draft) {
              draft.data = draft.data.filter((resource) => resource.id !== id)
            }
          })
      )

      return { previous }
    },
    onError: (_error, _params, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(queryKey)
    },
  })
}
